"use strict";

const LidToDoList = function () {
  const lidMissingInformationToDoLabels = {
    street_address: "Street Address",
    associated_bmps: "sBMPs",
    contact: "O&M Contact or Property Owner/LRP",
  };

  const organizeMaintenanceNotices = function (data) {
    for (const datum of data) {
      organizeCommonData(datum, datum.lidMaintenanceNotice);
      datum.ownerName = datum.lidMaintenanceNotice.additionalInfo?.ownerName;
      datum.bmpsCount = datum.lidMaintenanceNotice.additionalInfo?.bmpsCount;
    }

    return data;
  };

  const organizeInspections = function (data) {
    for (const datum of data) {
      organizeCommonData(datum, datum.lidInspection);
      datum.bmpInspectionProgressPercentage =
        datum.lidInspection.additionalInfo?.bmpInspectionProgressPercentage;
      datum.lastLidNoticeProjectContactName =
        datum.lidInspection.additionalInfo?.lastLidNoticeProjectContactName;
      datum.bmpsCount = datum.lidInspection.additionalInfo?.bmpsCount;
    }

    return data;
  };

  const organizeReInspections = function (data) {
    for (const datum of data) {
      organizeCommonData(datum, datum.lidReInspection);
      datum.bmpInspectionProgressPercentage =
        datum.lidReInspection.additionalInfo?.bmpInspectionProgressPercentage;
      datum.lastLidNoticeProjectContactName =
        datum.lidReInspection.additionalInfo?.lastLidNoticeProjectContactName;
      datum.bmpsCount = datum.lidReInspection.additionalInfo?.bmpsCount;
    }

    return data;
  };

  const organizeMaintenanceResponses = function (data) {
    const result = Misc.shallowCloneArrayObjects(data);

    for (const datum of result) {
      datum.lidMaintenanceResponse = structuredClone(datum.lidMaintenanceResponse);
      ToDoFunctions.flattenAdditionalInfoAndCloneExtrasOntoEndOfArray(
        result,
        datum,
        "lidMaintenanceResponse",
      );

      const additionalInfo = datum.lidMaintenanceResponse.additionalInfo;
      organizeCommonData(datum, withLetterDueDate(datum.lidMaintenanceResponse));
      datum.lastLidNoticeProjectContactName = additionalInfo?.lastLidNoticeProjectContactName;
      datum.bmpsCount = additionalInfo?.bmpsCount;
      datum.lidSendNoticeId = additionalInfo?.lidSendNoticeId;
      datum.noticeSentDate = additionalInfo?.sentDate;
    }

    return result;
  };

  // Each outstanding letter has its own response due date, while the to do row itself carries only
  // the newest one. Falls back to the row when a letter has no date of its own.
  const withLetterDueDate = function (toDoData) {
    const letterDueDate = toDoData.additionalInfo?.dueDate;

    if (!letterDueDate) {
      return toDoData;
    }

    return { ...toDoData, dueDate: letterDueDate, dueDateIso: letterDueDate };
  };

  const organizeReviewResponses = function (data) {
    for (const datum of data) {
      organizeCommonData(datum, datum.lidReviewResponse);
      datum.lastLidNoticeProjectContactName =
        datum.lidReviewResponse.additionalInfo?.lastLidNoticeProjectContactName;
      datum.bmpsCount = datum.lidReviewResponse.additionalInfo?.bmpsCount;
    }

    return data;
  };

  const organizeMissingInformation = function (data) {
    for (const datum of data) {
      organizeCommonData(datum, datum.lidMissingInformation);
      datum.additionalInfo = datum.lidMissingInformation?.additionalInfo;
      addDisplayRequiredFields(datum, datum.additionalInfo || []);
      datum.showMissingInformation = UserRoles.getConfig("showMissingInformation");
    }

    return data;
  };

  const organizeFlaggedForNewEnforcementTodos = function (data) {
    for (const datum of data) {
      organizeCommonData(datum, datum.lidProjectFlaggedForNewEnforcement);
      datum.displayResponsibleParty = Misc.getOptionNameByValue(
        ProjectInventoryConstants.ownershipOptions,
        datum.maintainerType,
      );
    }

    return data;
  };

  const organizeProjectEnforcements = function (data) {
    const result = [];
    for (const datum of data) {
      const todoData = datum.lidProjectEnforcement;
      for (const enforcement of todoData?.additionalInfo || []) {
        TableDataFunctions.assignLidProjectIcon(datum, true);
        const issueDate = enforcement.issueDate
          ? DateTime.parseIsoDate(enforcement.issueDate)
          : null;

        if (!enforcement.currentEscalation) continue;

        result.push({
          id: datum.id,
          enforcementId: enforcement.id,
          todoId: todoData.todoId,
          tableIcon: datum.tableIcon,
          tierNumber: datum.tierNumber,
          projectIdentifier: datum.projectIdentifier,
          name: datum.projectName,
          streetAddress: datum.streetAddress,
          displayResponsibleParty: Misc.getOptionNameByValue(
            ProjectInventoryConstants.ownershipOptions,
            datum.maintainerType,
          ),
          displayEnforcementReason: _getDisplayEnforcementReason(enforcement.currentEscalation),
          displayLevel: Misc.getOptionNameByValue(
            FormSettings.getDropdownOptions("level", { tool: "indcom" }),
            enforcement.currentEscalation?.level,
          ),
          fine: enforcement.currentEscalation?.fine,
          issueDate: issueDate,
          displayIssueDate: issueDate ? DateTime.dateToGroupDisplayDate(issueDate) : issueDate,
          assignee: todoData.assignee,
        });
      }
    }

    return result;
  };

  const organizeRecommendedActionTodos = function (data) {
    const result = [];
    for (const datum of data) {
      const todoData = datum.lidProjectRecommendedAction;
      for (const enforcement of todoData?.additionalInfo || []) {
        TableDataFunctions.assignLidProjectIcon(datum, true);
        const issueDate = enforcement.issueDate
          ? DateTime.parseIsoDate(enforcement.issueDate)
          : null;

        if (!enforcement.currentEscalation) continue;

        result.push({
          id: datum.id,
          enforcementId: enforcement.id,
          todoId: todoData.todoId,
          tableIcon: datum.tableIcon,
          projectIdentifier: datum.projectIdentifier,
          name: datum.projectName,
          streetAddress: datum.streetAddress,
          displayResponsibleParty: Misc.getOptionNameByValue(
            ProjectInventoryConstants.ownershipOptions,
            datum.maintainerType,
          ),
          displayEnforcementReason: _getDisplayEnforcementReason(enforcement.currentEscalation),
          displayLevel: Misc.getOptionNameByValue(
            FormSettings.getDropdownOptions("level", { tool: "indcom" }),
            enforcement.currentEscalation?.level,
          ),
          fine: enforcement.currentEscalation?.fine,
          issueDate: issueDate,
          displayIssueDate: issueDate ? DateTime.dateToGroupDisplayDate(issueDate) : issueDate,
          displayRecommendedAction: BaseEnforcementAction().escalationActionOptions.find(
            (option) => option.value === enforcement.recommendedAction,
          )?.name,
          recommendedAction: enforcement.recommendedAction,
          assignee: todoData.assignee,
        });
      }
    }

    return result;
  };

  const _getDisplayEnforcementReason = function (escalation) {
    const displayEnforcementReason = Misc.getOptionNameByValue(
      FormSettings.getDropdownOptions("enforcement-reason", { tool: "lid" }),
      escalation.enforcementReason,
    );
    const count = escalation.reasonCount ? ` (${escalation.reasonCount})` : "";

    return displayEnforcementReason + count;
  };

  const addDisplayRequiredFields = function (toDo, requiredFields) {
    toDo.displayRequiredFields = getLidMissingInformationDisplayFields(requiredFields);
    toDo.numMissingRequiredFields = toDo.displayRequiredFields.length;
  };

  var getLidMissingInformationDisplayFields = function (fields) {
    return fields.map((field) => getLidMissingInformationFieldDisplayName(field));
  };

  var getLidMissingInformationFieldDisplayName = function (field) {
    const readableField = lidMissingInformationToDoLabels[field];
    if (readableField === undefined) {
      throw new Error(`Unknown required field: ${field}`);
    }
    return readableField;
  };

  const organizeCommonData = function (datum, todoData) {
    datum.name = datum.projectIdentifier;
    datum.tierNumber = LidIcon.getLidProjectTierNumber(datum);
    datum.iconClass = LidProjectStyles.getNewLidProjectIconClass(datum, true);
    ToDoFunctions.addCommonFields(datum, todoData);
  };

  const canInventory = function (user) {
    return UserRoles.getConfigForUser("inventory", user);
  };

  function getSortersDict() {
    const dateSorter = DateTime.dateComparator;
    const alphaSorter = SortUtilities.alphaSorter;
    const numberSorter = SortUtilities.numberSorter;

    return {
      assignee: ToDoUserPicker.assigneeSort,
      projectIdentifier: alphaSorter,
      projectName: alphaSorter,
      name: alphaSorter,
      ownerName: alphaSorter,
      lastLidNoticeProjectContactName: alphaSorter,
      streetAddress: alphaSorter,
      displayResponsibleParty: alphaSorter,
      displayEnforcementReason: alphaSorter,
      displayLevel: alphaSorter,
      fine: numberSorter,
      issueDate: dateSorter,
      dueDate: dateSorter,
      // Sorted as a string: dateComparator demands Date objects, and these stay ISO strings so
      // the template can format them. Uniform ISO-8601 UTC sorts chronologically either way.
      noticeSentDate: alphaSorter,
      bmpsCount: numberSorter,
      bmpInspectionProgressPercentage: (a, b) => numberSorter(b, a),
      numMissingRequiredFields: numberSorter,
    };
  }

  function canEnforceProject(user) {
    return user.permissions.can_issue_lid_enforcement;
  }

  const loadMissingInformationListeners = function ($container) {
    $container.off("click", ".project-inventory-button");
    $container.on("click", ".project-inventory-button", showProjectInventoryModal);
  };

  var showProjectInventoryModal = function () {
    const id = $(this).parents("tr").data("id");
    ProjectInventoryModalController.invokeProjectInventoryModal(id);
  };

  const loadFormalCommunicationsListeners = function ($container, { selectedTable }) {
    const onFormalCommunicationsClear = function (e) {
      // Stop the underlying header from collapsing/expanding
      e.stopPropagation();
      const checkedRowIds = selectedTable
        .getAllSelectedRows()
        .toArray()
        .map((elm) => $(elm).data("id"));

      _clearToDos(checkedRowIds, "maintenance notice", "lid-maintenance-notice");
    };
    $container.off(
      "click",
      ".js-lid-formal-communications-clear-selected",
      onFormalCommunicationsClear,
    );
    $container.on(
      "click",
      ".js-lid-formal-communications-clear-selected",
      onFormalCommunicationsClear,
    );

    const onGenerateLetter = function (e) {
      onLetterClick($(e.currentTarget), selectedTable, { action: "send" });
    };
    $container.off("click", ".js-lid-generate", onGenerateLetter);
    $container.on("click", ".js-lid-generate", onGenerateLetter);

    $container.off("click", ".js-open-fact-sheet-to-assets", openFactSheetToAssets);
    $container.on("click", ".js-open-fact-sheet-to-assets", openFactSheetToAssets);

    const toggleActionButtons = function () {
      _toggleHeaderActionButton($container, selectedTable.getAllSelectedRows().length > 0);
    };
    selectedTable.addListener(toggleActionButtons);
    toggleActionButtons();
  };

  const loadFlaggedForEnforcementListeners = function ($container, { selectedTable }) {
    const onFlaggedInspectionsClear = function (e) {
      e.stopPropagation();
      const checkedRowIds = selectedTable
        .getAllSelectedRows()
        .toArray()
        .map((elm) => $(elm).data("id"));

      ConstructionToDoController.clearLidFlaggedInspections(checkedRowIds);
    };

    $container.off("click", ".js-clear-flagged-inspections", onFlaggedInspectionsClear);
    $container.on("click", ".js-clear-flagged-inspections", onFlaggedInspectionsClear);
  };

  const loadMaintenanceResponseListeners = function ($container, { selectedTable }) {
    const onResponseNeededClear = function (e) {
      // Stop the underlying header from collapsing/expanding
      e.stopPropagation();
      const checkedRowIds = selectedTable
        .getAllSelectedRows()
        .toArray()
        .map((elm) => $(elm).data("id"));
      _clearToDos(checkedRowIds, "maintenance response", "lid-maintenance-response");
    };
    $container.off("click", ".js-lid-response-needed-clear-selected", onResponseNeededClear);
    $container.on("click", ".js-lid-response-needed-clear-selected", onResponseNeededClear);

    const onUpdateResponseClick = function (e) {
      onLetterClick($(e.currentTarget), selectedTable);
    };
    $container.off("click", ".js-lid-update-response", onUpdateResponseClick);
    $container.on("click", ".js-lid-update-response", onUpdateResponseClick);

    $container.off("click", ".js-open-fact-sheet-to-assets", openFactSheetToAssets);
    $container.on("click", ".js-open-fact-sheet-to-assets", openFactSheetToAssets);

    const toggleActionButtons = function () {
      _toggleHeaderUpdateButton($container, selectedTable.getAllSelectedRows());
    };
    selectedTable.addListener(toggleActionButtons);
    toggleActionButtons();
  };

  const loadReviewResponseListeners = function ($container, { selectedTable }) {
    const onUpdateResponseClick = function (e) {
      onLetterClick($(e.currentTarget));
    };
    $container.off("click", ".js-lid-update-response", onUpdateResponseClick);
    $container.on("click", ".js-lid-update-response", onUpdateResponseClick);

    $container.off("click", ".js-open-fact-sheet-to-assets", openFactSheetToAssets);
    $container.on("click", ".js-open-fact-sheet-to-assets", openFactSheetToAssets);
  };

  const loadInspectionListeners = function ($container, { selectedTable }) {
    $container.off("click", ".js-lid-generate", onInspectionClick);
    $container.on("click", ".js-lid-generate", onInspectionClick);

    $container.off("click", ".js-open-fact-sheet-to-assets", openFactSheetToAssets);
    $container.on("click", ".js-open-fact-sheet-to-assets", openFactSheetToAssets);
  };

  const loadReInspectionListeners = function ($container, { selectedTable }) {
    $container.off("click", ".js-lid-generate", onInspectionClick);
    $container.on("click", ".js-lid-generate", onInspectionClick);

    $container.off("click", ".js-open-fact-sheet-to-assets", openFactSheetToAssets);
    $container.on("click", ".js-open-fact-sheet-to-assets", openFactSheetToAssets);
  };

  const _clearToDos = async function (rowIds, selectedToDosName, apiSubject) {
    try {
      await ToDoFunctions.showConfirmClearToDoListConfirm(rowIds.length, {
        selectedToDosName,
        confirmButton: "Remove Tasks",
      });
    } catch (e) {
      // User canceled message
      return;
    }

    await ApiCalls.deleteConstructionProjectToDos(rowIds, apiSubject);
    PostConstructionProjectGeoServerLayer.invalidateLayerData();
  };

  const _toggleHeaderActionButton = function ($table, showButton) {
    const buttonHtml = `<a class="js-lid-generate">Generate</a>`;
    _toggleHeaderButton($table, buttonHtml, showButton);
  };

  const _toggleHeaderUpdateButton = function ($table, $selectedRows) {
    const showButton = $selectedRows.length > 0;
    const buttonHtml = `<a class="js-lid-update-response">Follow Up</a>`;
    _toggleHeaderButton($table, buttonHtml, showButton);
  };

  const _toggleHeaderButton = function ($table, buttonHtml, showButton) {
    const normalHtml = `Actions`;
    $table.find("th.actions-col").html(showButton ? buttonHtml : normalHtml);
  };

  const onLetterClick = function ($element, selectedTable = null, options = {}) {
    const projectId = $element.closest("tr").data("id");
    let projectIds = selectedTable
      ? selectedTable
          .getAllSelectedRows()
          .toArray()
          .map((element) => $(element).data("id"))
      : [];

    if (projectIds.length === 0) {
      projectIds = [projectId];
    }

    LetterCommunicationModal.renderAndShow(projectIds, options);
  };

  const onInspectionClick = function (e) {
    InspectionSummaryLetterModal.renderAndShow([$(e.currentTarget).closest("tr").data("id")]);
  };

  const handleConditionPhaseToggleDisplay = function (activeTab) {
    $(".condition-phase-toggle").toggle(activeTab !== "todo");
    $(".condition-label").toggle(activeTab === "todo");

    const toggle =
      activeTab === "todo" ? "condition" : Tree.get(["filters", "conditionPhaseToggle"]);
    PostConstructionProjectGeoServerLayer.handleLegendDisplayByConditionPhaseToggle(toggle);
  };

  const openFactSheetToAssets = function (e) {
    ToDoListController.openFactSheetTo($(e.currentTarget).closest("tr").data("id"), {
      highlightId: "assets-header",
    });
  };

  return {
    organizeMissingInformation,
    organizeFlaggedForNewEnforcementTodos,
    organizeProjectEnforcements,
    organizeRecommendedActionTodos,
    organizeMaintenanceNotices,
    organizeInspections,
    organizeReInspections,
    organizeMaintenanceResponses,
    organizeReviewResponses,
    canInventory,
    canEnforceProject,
    getSortersDict,
    loadFormalCommunicationsListeners,
    loadMaintenanceResponseListeners,
    loadReviewResponseListeners,
    loadInspectionListeners,
    loadReInspectionListeners,
    loadFlaggedForEnforcementListeners,
    loadMissingInformationListeners,
    addDisplayRequiredFields,
    _toggleHeaderActionButton,
    handleConditionPhaseToggleDisplay,
  };
};

module.exports = LidToDoList();

const UserRoles = require("../login/userRoles");
const LidIcon = require("./lidIcon");
const LidProjectStyles = require("./lidProjectStyles");
const ToDoFunctions = require("../mapFunctions/toDoFunctions");
const ApiCalls = require("../apiCalls");
const ToDoListController = require("../mapFunctions/toDoListController");
const PostConstructionProjectGeoServerLayer = require("../constructionG2/postConstructionProjectGeoServerLayer");
const ProjectInventoryModalController = require("../construction/projectInventoryModalController");
const LetterCommunicationModal = require("./letterCommunicationModal");
const Tree = require("../tree");
const DateTime = require("../dateTime");
const Misc = require("../misc");
const FormSettings = require("../settings/formSettings");
const SortUtilities = require("../general/sortUtilities");
const ProjectInventoryConstants = require("../construction/projectInventoryConstants");
const TableDataFunctions = require("../general/tableDataFunctions");
const InspectionSummaryLetterModal = require("./inspectionSummaryLetterModal");
const BaseEnforcementAction = require("../enforcement/baseEnforcementAction");
const ConstructionToDoController = require("../construction/constructionToDoController");
const ToDoUserPicker = require("../general/toDoUserPicker");
